<template>
  <div class="modal-overlay" @click="$emit('close')">
    <div class="modal-content affectation-modal" @click.stop>
      <div class="modal-header">
        <h4>
          <i class="fas fa-user-plus"></i>
          Affecter un Nouveau Technicien
        </h4>
        <button @click="$emit('close')" class="close-btn">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="modal-body">
        <!-- Informations du prospect -->
        <div class="prospect-info-card">
          <div class="info-row">
            <span class="label">Client:</span>
            <span class="value">{{ prospect?.client?.nomComplet }}</span>
          </div>
          <div class="info-row">
            <span class="label">Téléphone:</span>
            <span class="value">{{ prospect?.client?.telephone }}</span>
          </div>
          <div class="info-row">
            <span class="label">Type:</span>
            <span class="value">{{ prospect?.typeIntervention }}</span>
          </div>
          <div class="info-row">
            <span class="label">Priorité:</span>
            <span :class="['value', 'priority-badge', getPriorityClass(prospect?.priorite)]">
              {{ prospect?.priorite }}
            </span>
          </div>
        </div>

        <!-- Sélection du technicien -->
        <div class="form-group">
          <label class="form-label required">
            <i class="fas fa-user-cog"></i>
            Technicien
          </label>
          <select v-model="selectedTechnicien" class="form-control" required>
            <option value="">Sélectionner un technicien</option>
            <option v-for="tech in techniciens" :key="tech._id" :value="tech._id">
              {{ tech.prenom }} {{ tech.nom }}
            </option>
          </select>
        </div>

        <!-- Nouvelle date (optionnelle) -->
        <div class="form-group">
          <label class="form-label">
            <i class="fas fa-calendar-alt"></i>
            Nouvelle Date (optionnelle)
          </label>
          <div v-if="nouvelleDate" class="date-info-message">
            <i class="fas fa-info-circle"></i>
            Date proposée lors du report. Vous pouvez la modifier si nécessaire.
          </div>
          <input 
            type="date" 
            v-model="nouvelleDate" 
            :min="minDate"
            class="form-control"
          />
        </div>

        <!-- Nouvelle heure de début -->
        <div class="form-group" v-if="nouvelleDate">
          <label class="form-label required">
            <i class="fas fa-clock"></i>
            Heure de début
          </label>
          <input 
            type="time" 
            v-model="nouvelleHeureDebut" 
            class="form-control"
            required
          />
        </div>

        <!-- Nouvelle heure de fin -->
        <div class="form-group" v-if="nouvelleDate">
          <label class="form-label required">
            <i class="fas fa-clock"></i>
            Heure de fin
          </label>
          <input 
            type="time" 
            v-model="nouvelleHeureFin" 
            class="form-control"
            required
          />
        </div>
      </div>

      <div class="modal-footer">
        <button @click="$emit('close')" class="btn btn-secondary">
          <i class="fas fa-arrow-left"></i>
          Annuler
        </button>
        <button 
          @click="confirmer" 
          class="btn btn-success"
          :disabled="!selectedTechnicien || (nouvelleDate && (!nouvelleHeureDebut || !nouvelleHeureFin))"
        >
          <i class="fas fa-check"></i>
          Confirmer l'affectation
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'

export default {
  name: 'AffectationTechnicienModal',
  props: {
    prospect: {
      type: Object,
      required: true
    }
  },
  emits: ['close', 'confirm'],
  setup(props, { emit }) {
    const selectedTechnicien = ref('')
    const nouvelleDate = ref('')
    const nouvelleHeureDebut = ref('09:00')
    const nouvelleHeureFin = ref('12:00')
    const techniciens = ref([])

    // Initialiser les valeurs avec les données du prospect
    const initializeValues = () => {
      // Pré-remplir le technicien si déjà affecté
      if (props.prospect.technicienAffecte) {
        selectedTechnicien.value = props.prospect.technicienAffecte._id || props.prospect.technicienAffecte
      }

      // Pré-remplir la date si elle existe dans datesProposes ou rdvExistant
      if (props.prospect.rdvExistant) {
        // Utiliser la date du RDV existant
        const dateRdv = new Date(props.prospect.rdvExistant.dateRdv)
        nouvelleDate.value = dateRdv.toISOString().split('T')[0]
        nouvelleHeureDebut.value = props.prospect.rdvExistant.heureDebut || '09:00'
        nouvelleHeureFin.value = props.prospect.rdvExistant.heureFin || '12:00'
      } else if (props.prospect.datesProposes && props.prospect.datesProposes.length > 0) {
        // Utiliser la première date proposée
        const premiereDatePropose = props.prospect.datesProposes[0]
        const dateSortie = premiereDatePropose.dateSortie || premiereDatePropose.date
        if (dateSortie) {
          const date = new Date(dateSortie)
          nouvelleDate.value = date.toISOString().split('T')[0]
        }
        nouvelleHeureDebut.value = premiereDatePropose.heureDebut || '09:00'
        nouvelleHeureFin.value = premiereDatePropose.heureFin || '12:00'
      }
    }

    const minDate = computed(() => {
      const today = new Date()
      return today.toISOString().split('T')[0]
    })

    const loadTechniciens = async () => {
      try {
        const token = localStorage.getItem('token')
        const response = await fetch('/api/prospects/techniciens', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
        if (response.ok) {
          const data = await response.json()
          techniciens.value = data.techniciens || []
        }
      } catch (error) {
        console.error('Erreur lors du chargement des techniciens:', error)
      }
    }

    const getPriorityClass = (priorite) => {
      const classes = {
        'Haute': 'priority-high',
        'Moyenne': 'priority-medium',
        'Basse': 'priority-low'
      }
      return classes[priorite] || ''
    }

    const confirmer = () => {
      if (!selectedTechnicien.value) {
        return
      }

      if (nouvelleDate.value && (!nouvelleHeureDebut.value || !nouvelleHeureFin.value)) {
        return
      }

      emit('confirm', {
        technicienAffecte: selectedTechnicien.value,
        nouvelleDate: nouvelleDate.value || null,
        nouvelleHeureDebut: nouvelleHeureDebut.value || null,
        nouvelleHeureFin: nouvelleHeureFin.value || null
      })
    }

    onMounted(() => {
      initializeValues()
      loadTechniciens()
    })

    return {
      selectedTechnicien,
      nouvelleDate,
      nouvelleHeureDebut,
      nouvelleHeureFin,
      techniciens,
      minDate,
      getPriorityClass,
      confirmer
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1rem;
}

.modal-content {
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  border-radius: 16px 16px 0 0;
}

.modal-header h4 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.close-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  color: white;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: rotate(90deg);
}

.modal-body {
  padding: 1.5rem;
}

.prospect-info-card {
  background: linear-gradient(135deg, #f8fafc, #f1f5f9);
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  border: 1px solid #e2e8f0;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #e2e8f0;
}

.info-row:last-child {
  border-bottom: none;
}

.info-row .label {
  font-weight: 600;
  color: #64748b;
  font-size: 0.9rem;
}

.info-row .value {
  color: #1e293b;
  font-weight: 500;
}

.priority-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 600;
}

.priority-high {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
}

.priority-medium {
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: white;
}

.priority-low {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
}

.form-group {
  margin-bottom: 1.25rem;
}

.date-info-message {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background: linear-gradient(135deg, #dbeafe, #eff6ff);
  border-left: 3px solid #3b82f6;
  border-radius: 8px;
  margin-bottom: 0.75rem;
  font-size: 0.9rem;
  color: #1e40af;
}

.date-info-message i {
  color: #3b82f6;
  font-size: 1rem;
}

.form-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
  font-size: 0.95rem;
}

.form-label.required::after {
  content: '*';
  color: #ef4444;
  margin-left: 0.25rem;
}

.form-control {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.95rem;
  transition: all 0.3s ease;
}

.form-control:focus {
  outline: none;
  border-color: var(--primary-blue);
  box-shadow: 0 0 0 3px rgba(0, 27, 183, 0.1);
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
  border-radius: 0 0 16px 16px;
}

.btn {
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-secondary {
  background: #6b7280;
  color: white;
}

.btn-secondary:hover {
  background: #4b5563;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(107, 114, 128, 0.3);
}

.btn-success {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
}

.btn-success:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.btn-success:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .modal-content {
    max-width: 100%;
    margin: 0;
    border-radius: 0;
    max-height: 100vh;
  }

  .modal-header {
    border-radius: 0;
  }

  .modal-footer {
    flex-direction: column;
    border-radius: 0;
  }

  .btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
